import UserAPI, { User } from './api'
import { Context } from '../server-middleware/context'
import { ContentType } from '../preact-components/utils/contentType'

export interface Author {
  id: number
  url: string
  name: string
  avatar: string
  userTitle?: string
}

export interface IdeationStatus {
  name: string
  bgColor: string
  color: string
}

export interface TopicFromUnified {
  contentType: ContentType
  id: number
  publicId: number
  title: string
  url: string
  featuredImage: string
  label: string
  replyCount: number
  views: number
  votes: string[]
  content: string
  createdAt: string
  lastActivityAt: string
  author: Author
  publicLabel?: string
  productAreas: string[]
  likes: number
  likeSet: number[]
  voteCount: number
  ideationStatus?: IdeationStatus
  publishedAt: string
}

export interface UnifiedQuery {
  page: number
  pageSize: number
  orderBy: 'lastActivityAt' | 'replyCount' | 'views'
  pendingTopics?: boolean | undefined
  contentType?: ContentType | undefined
  hasBestAnswer?: boolean | undefined
  publicIds?: Array<string|number> | undefined
}

const TYPE_PRODUCT_UPDATE: ContentType = 'productUpdate'

export interface FeaturedQuestion extends TopicFromUnified {
  contentType: 'question'
}

export interface FeaturedConversation extends TopicFromUnified {
  contentType: 'conversation'
}

export interface FeaturedArticle extends TopicFromUnified {
  contentType: 'article'
}

export interface FeaturedIdea extends TopicFromUnified {
  contentType: 'idea'
  ideationStatus: IdeationStatus
  hasCurrentUserVoted: boolean
}

export interface FeaturedProductUpdate extends TopicFromUnified{
  contentType: 'productUpdate'
}

export type FeaturedTopicFromUnified = FeaturedProductUpdate | FeaturedIdea | FeaturedArticle | FeaturedConversation | FeaturedQuestion

export interface UnifiedTopic {
  id: number
  publicId: number
  title: string
  content: string
  contentType: 'conversation' | 'question' | 'article' | 'idea' | 'productUpdate'
  featuredImage: string
  lastActivityAt: string
  publishedAt: string
  createdAt: string
  productAreas: string[]
  replyCount: number
  likes: number
  likeSet: number[]
  votes: number
  voteSet: string[]
  views: number
  label: string
  publicLabel: string
  voteCount: number
  trashed: boolean
  status: string
  ideaStatus?: {
    name: string
    color: string
    bgColor: string
  }
  author: {
    id: number
  }
  sticky: boolean
  lastPostId: string
  lastContributor: {
    id: number
  }
  categoryId: string
  categoryName: string
}

interface UnifiedResponse {
  data: {
    result: UnifiedTopic[]
  }
}

interface UnifiedInternalResponse {
  data: {
    result: {
      topics: UnifiedTopic[]
    }
  }
}

interface ProductUpdateIdMap {
  [id: string]: FeaturedProductUpdate | undefined
}

interface TopicIdMap {
  [id: string]: FeaturedTopicFromUnified | undefined
}

class Unified {
  context: Context
  url: string
  api: UserAPI
  UNIFIED_GET_INTERNAL_TOPICS = '/v2/internal/topics'
  UNIFIED_GET_TOPICS = '/v2/topics'

  constructor (context: Context) {
    this.context = context
    const host = process.env.app_config_unified_api_url
    this.url = host
      ? (host.substr(0, 4) === 'http' ? host : `https://${host}`)
      : 'http://unified.dev.insided.com'
    this.api = new UserAPI(context)
  }

  private userToAuthor (u: User): Author {
    return {
      avatar: u.avatar,
      id: u.userid,
      name: u.username,
      url: `/members/${u.username.toLowerCase()}-${u.userid}`,
      userTitle: u.usertitle
    }
  }

  public async getProductUpdatesIndexedById (topicIds: Array<string|number>) {
    if (!topicIds.length) {
      return {}
    }

    const topicInfoResult: UnifiedResponse = await this.context.axios.get(`${this.url}${this.UNIFIED_GET_TOPICS}`, {
      headers: { 'X-Consumer-Custom-Id': this.context.yipId },
      params: {
        contentType: TYPE_PRODUCT_UPDATE,
        topicIds: topicIds
      }
    })

    const topics: ProductUpdateIdMap = {}
    await Promise.allSettled(topicInfoResult.data.result.map(async topic => {
      if (topic.status !== 'published') return
      if (topic.trashed) return
      if (topic.contentType !== 'productUpdate') return

      topics[topic.id] = {
        id: topic.id,
        publicId: topic.publicId,
        votes: topic.voteSet,
        likes: topic.likes,
        likeSet: topic.likeSet,
        createdAt: topic.createdAt,
        title: topic.title,
        content: topic.content,
        contentType: topic.contentType,
        featuredImage: topic.featuredImage,
        lastActivityAt: topic.lastActivityAt,
        publishedAt: topic.publishedAt,
        productAreas: topic.productAreas,
        replyCount: topic.replyCount,
        views: topic.views,
        label: topic.label,
        voteCount: topic.voteCount,
        publicLabel: topic.publicLabel,
        url: `product-updates/${topic.id}`,
        author: this.userToAuthor(await this.api.getUser(topic.author.id))
      }
    }))

    return topics
  }

  public async getTopicsIndexedByPublicId (topicIds: Array<string|number>): Promise<TopicIdMap> {
    if (!topicIds.length) {
      return {}
    }

    const topicInfoResult = await this.findByQuery({
      page: 1,
      pageSize: 25,
      orderBy: 'lastActivityAt',
      publicIds: topicIds
    })

    const topics: TopicIdMap = {}
    const promises = await Promise.allSettled(topicInfoResult.data.result.topics.map(async (topic) => {
      topics[topic.publicId] = {
        id: topic.id,
        publicId: topic.publicId,
        title: topic.title,
        content: topic.content,
        contentType: topic.contentType,
        likeSet: topic.likeSet,
        featuredImage: topic.featuredImage ?? null,
        lastActivityAt: topic.lastActivityAt,
        createdAt: topic.createdAt,
        productAreas: topic.productAreas ?? [],
        replyCount: topic.replyCount,
        likes: topic.likes,
        votes: topic.voteSet,
        voteCount: topic.voteCount,
        views: topic.views,
        label: topic.label ?? '',
        publicLabel: topic.publicLabel ?? '',
        publishedAt: topic.publishedAt,
        url: topic.contentType === 'idea' ? `ideas/${topic.publicId}` : `/fid-1/tid-${topic.publicId}`,
        ideationStatus: {
          name: topic.ideaStatus?.name || '',
          bgColor: `#${topic.ideaStatus?.bgColor}`,
          color: `#${topic.ideaStatus?.color}`
        },
        author: this.userToAuthor(await this.api.getUser(topic.author.id)),
        hasCurrentUserVoted: topic.voteSet.some(v => parseInt(v) === this.context.userId)
      }
    }))
    promises.forEach(p => {
      if (p.status === 'rejected') this.context.logger(p.reason)
    })

    return topics
  }

  public async findByQuery (query: UnifiedQuery): Promise<UnifiedInternalResponse> {
    return await this.context.axios.post(`${this.url}${this.UNIFIED_GET_INTERNAL_TOPICS}`, {
      filters: {
        trashed: false,
        status: ['published'],
        publicId: query.publicIds,
        hasBestAnswer: query.hasBestAnswer,
        contentType: query.contentType
      },
      sort: { [query.orderBy]: 'desc' },
      pagination: {
        page: query.page,
        pageSize: query.pageSize
      }
    }, {
      headers: { 'X-Consumer-Custom-Id': this.context.yipId }
    })
  }
}

export default Unified
